import { Button, Grid, styled, TextField, FormControl, InputLabel, Select, MenuItem, ListSubheader, Box, FormControlLabel, Switch } from '@mui/material';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { ObjectSchema } from 'yup';
import { FC, useEffect, useState } from 'react';
import { H2, H4 } from 'components/Typography';
import { useTranslation } from 'react-i18next';
import { KeyboardArrowDown } from '@mui/icons-material';
import {
  CalendarEntryEntityTypeEnum,
  CreateCalendarEntryDto,
  CustomCalendarEntryTypesEntity,
} from '../../../api/generated';
import { calendarEntriesApi, absenceTypesApi, tenantConfigApi, AbsenceTypeEntity } from '../../../api';
import AppModal from '../../../components/AppModal';
import FlexBox from '../../../components/flexbox/FlexBox';
import { format } from 'date-fns';
import CalendarInput from 'components/input-fields/CalendarInput';
import { isValidDate } from 'utils/dateValidator';
import { checkTokenRole } from 'utils/checkToken';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';

export type AddWorkFormData = Omit<
  CreateCalendarEntryDto,
  'startedOn' | 'endedOn' | 'type'
> & {
  startedOn: Date;
  endedOn: Date;
  type: CalendarEntryEntityTypeEnum | string;
  managerNotes: string | null | undefined;
  publicNotes: string | null | undefined;
  customTypeId?: number;
  allDay?: boolean;
  startTime?: string;
  endTime?: string;
  // Absence catalog (causali) — optional, only used when the catalog is active
  absenceTypeId?: number;
  certificateStatus?: string;
  protocolNumber?: string;
  certificateStartedOn?: Date | null;
  certificateEndedOn?: Date | null;
  quantityHours?: number;
};

interface ModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (values: AddWorkFormData) => Promise<void>;
  data?: Partial<AddWorkFormData>;
  employees?: any;
  skipOverlapCheckRef?: React.MutableRefObject<boolean>;
}

const StyledAppModal = styled(AppModal)(({ theme }) => ({
  maxWidth: 450,
  minWidth: 200,

  [theme.breakpoints.down(325)]: { maxWidth: '100%' },
}));

const AddEventModal: FC<ModalProps> = ({ open, onClose, onSubmit, data, employees, skipOverlapCheckRef }) => {
  const { t, i18n } = useTranslation();
  const userRole = checkTokenRole();
  const canViewManagerNotes = ['MANAGER', 'CONTROLLER', 'OWNER'].includes(userRole);
  const [customTypes, setCustomTypes] = useState<CustomCalendarEntryTypesEntity[]>([]);
  const [absenceTypes, setAbsenceTypes] = useState<AbsenceTypeEntity[]>([]);
  const [catalogActive, setCatalogActive] = useState<boolean>(false);

  const initialValues: AddWorkFormData = {
    startedOn: data?.startedOn ?? toUTC(new Date()),
    endedOn: data?.endedOn ?? toUTC(new Date()),
    type: data?.type ?? CalendarEntryEntityTypeEnum.DayOff,
    customTypeId: data?.customTypeId,
    allDay: data?.allDay ?? true,
    startTime: data?.startTime ?? '09:00',
    endTime: data?.endTime ?? '17:00',
    managerNotes: data?.managerNotes ?? '',
    publicNotes: data?.publicNotes ?? '',
    absenceTypeId: data?.absenceTypeId,
    certificateStatus: data?.certificateStatus,
    protocolNumber: data?.protocolNumber,
    certificateStartedOn: data?.certificateStartedOn ?? null,
    certificateEndedOn: data?.certificateEndedOn ?? null,
    quantityHours: data?.quantityHours,
  };

  const validationSchema = Yup.object().shape({
    startedOn: Yup.date().required(
      t('common.forms.field.required', {
        field: t('employees.calendar.entry.startedOn'),
      }),
    ).typeError(t('employees.calendar.typeError'))
      .test('is-valid-date', t('employees.calendar.typeError'), (value) => {
        return isValidDate(value);
      }),
    endedOn: Yup.date()
      .typeError(t('employees.calendar.typeError'))
      .required(t('common.forms.field.required', {
        field: t('employees.calendar.entry.endedOn'),
      })),
    type: Yup.string()
      .required(
        t('common.forms.field.required', {
          field: t('employees.calendar.entry.type.label'),
        }),
      ),
    customTypeId: Yup.number().when('type', {
      is: (type: string) => type === CalendarEntryEntityTypeEnum.Custom,
      then: (schema) => schema.required(t('common.forms.field.required', { field: t('company.customTypeName') })),
      otherwise: (schema) => schema.optional().nullable(),
    }),
    allDay: Yup.boolean().optional(),
    startTime: Yup.string().when('allDay', {
      is: false,
      then: (schema) => schema.required(t('employees.calendar.entry.startTimeRequired')),
      otherwise: (schema) => schema.optional(),
    }),
    endTime: Yup.string().when('allDay', {
      is: false,
      then: (schema) => schema.required(t('employees.calendar.entry.endTimeRequired')),
      otherwise: (schema) => schema.optional(),
    }).test('time-order', t('employees.calendar.entry.endTimeAfterStartTime'), function (value) {
      const { startTime, allDay } = this.parent;
      if (allDay || !startTime || !value) return true;

      const startTimeObj = new Date(`2000-01-01T${startTime}`);
      const endTimeObj = new Date(`2000-01-01T${value}`);

      return endTimeObj > startTimeObj;
    }),
    managerNotes: Yup.string().notRequired(),
    publicNotes: Yup.string().notRequired(),
    absenceTypeId: Yup.number().optional().nullable(),
    certificateStatus: Yup.string().optional().nullable(),
    protocolNumber: Yup.string().optional().nullable(),
    certificateStartedOn: Yup.date().optional().nullable(),
    certificateEndedOn: Yup.date().optional().nullable(),
    quantityHours: Yup.number().optional().nullable(),
  }) as ObjectSchema<AddWorkFormData>;

  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit,
  });
  const { values, errors, handleChange, handleBlur, handleSubmit, touched, setFieldValue } =
    formik;


  // Fetch custom types when modal opens
  useEffect(() => {
    if (open) {
      fetchCustomTypes();
      fetchAbsenceCatalog();
    }
  }, [open]);

  // Update form values when modal opens with new data
  useEffect(() => {
    if (open && data) {
      if (data.startedOn) {
        setFieldValue('startedOn', toUTC(data.startedOn));
      }
      if (data.endedOn) {
        setFieldValue('endedOn', toUTC(data.endedOn));
      }
      if (data.allDay !== undefined) {
        setFieldValue('allDay', data.allDay);
      }
      if (data.startTime) {
        setFieldValue('startTime', data.startTime);
      }
      if (data.endTime) {
        setFieldValue('endTime', data.endTime);
      }
      if (data.type) {
        setFieldValue('type', data.type);
      }
      if (data.customTypeId !== undefined) {
        setFieldValue('customTypeId', data.customTypeId);
      }
      if (data.managerNotes !== undefined) {
        setFieldValue('managerNotes', data.managerNotes);
      }
      if (data.publicNotes !== undefined) {
        setFieldValue('publicNotes', data.publicNotes);
      }
    }
  }, [open, data, setFieldValue]);

  const fetchCustomTypes = async () => {
    try {
      const response = await calendarEntriesApi.getAllCustomTypes();
      setCustomTypes(response.data);
    } catch (error) {
      console.error('Failed to fetch custom types:', error);
    }
  };

  // Load the absence "causali" catalog only when the tenant has it enabled.
  const fetchAbsenceCatalog = async () => {
    try {
      const cfg: { data: any } = await tenantConfigApi.findConfigs();
      const active = Boolean(cfg.data?.isAbsenceCatalogActive);
      setCatalogActive(active);
      if (active) {
        const response = await absenceTypesApi.findAll();
        setAbsenceTypes(response.data ?? []);
      }
    } catch (error) {
      console.error('Failed to fetch absence catalog:', error);
    }
  };

  // Localized label for a causale, falling back to English.
  const lang = (i18n.language || 'en').slice(0, 2);
  const absenceLabel = (a: AbsenceTypeEntity) =>
    lang === 'it' ? a.labelIt : lang === 'de' ? a.labelDe : lang === 'fr' ? a.labelFr : a.labelEn;
  const selectedAbsenceType = absenceTypes.find((a) => a.id === values.absenceTypeId);

  useEffect(() => {
    if (values.type !== CalendarEntryEntityTypeEnum.Custom && values.customTypeId !== undefined) {
      setFieldValue('customTypeId', undefined);
    }
  }, [values.type, values.customTypeId, setFieldValue]);



  function toUTC(date: Date) {
    // Create a date at local midnight to avoid timezone issues
    const localDate = new Date(date);
    localDate.setHours(0, 0, 0, 0);
    return localDate;
  }

  // Get default event types (excluding Custom)
  const defaultEventTypes = Object.values(CalendarEntryEntityTypeEnum).filter(
    type => type !== CalendarEntryEntityTypeEnum.Custom
  );
  // Build a stable UI value that is ALWAYS a string
  const uiSelectValue =
    values.absenceTypeId != null
      ? `absence:${values.absenceTypeId}`
      : values.type === CalendarEntryEntityTypeEnum.Custom
        ? (values.customTypeId != null ? `custom:${values.customTypeId}` : '')
        : `builtin:${values.type}`;

  // Clears all absence-catalog fields (used when switching back to a legacy type).
  const clearedAbsenceFields = {
    absenceTypeId: undefined,
    certificateStatus: undefined,
    protocolNumber: '',
    certificateStartedOn: null,
    certificateEndedOn: null,
    quantityHours: undefined,
  };

  // Single onChange that maps UI value -> Formik state
  const handleTypeChangeUi = (event: any) => {
    const raw = String(event.target.value || '');

    if (raw.startsWith('absence:')) {
      const id = Number(raw.slice('absence:'.length));
      const at = absenceTypes.find((a) => a.id === id);
      formik.setValues(
        {
          ...formik.values,
          // Mirror the causale's legacyType into `type` so the rest of the app keeps working.
          type: (at?.legacyType as CalendarEntryEntityTypeEnum) ?? CalendarEntryEntityTypeEnum.DayOff,
          customTypeId: undefined,
          absenceTypeId: Number.isFinite(id) ? id : undefined,
          certificateStatus: at?.requiresCertificate ? 'PENDING' : undefined,
          protocolNumber: '',
          certificateStartedOn: null,
          certificateEndedOn: null,
          quantityHours: undefined,
        },
        false
      );
    } else if (raw.startsWith('builtin:')) {
      const enumVal = raw.slice('builtin:'.length) as CalendarEntryEntityTypeEnum;
      formik.setValues(
        {
          ...formik.values,
          type: enumVal,
          customTypeId: undefined,
          ...clearedAbsenceFields,
        },
        false
      );
    } else if (raw.startsWith('custom:')) {
      const id = Number(raw.slice('custom:'.length));
      formik.setValues(
        {
          ...formik.values,
          type: CalendarEntryEntityTypeEnum.Custom,
          customTypeId: Number.isFinite(id) ? id : undefined,
          ...clearedAbsenceFields,
        },
        false
      );
    } else {
      // fallback to empty
      formik.setValues(
        {
          ...formik.values,
          type: CalendarEntryEntityTypeEnum.DayOff, // or whatever default you want
          customTypeId: undefined,
          ...clearedAbsenceFields,
        },
        false
      );
    }
  };
  const optionsKey = [...customTypes.map(ct => ct.id), ...absenceTypes.map(a => `a${a.id}`)].join(',');

  const selectValue =
    values.type === CalendarEntryEntityTypeEnum.Custom
      ? (values.customTypeId != null ? `custom-${values.customTypeId}` : '')
      : (values.type);
  // build a flat items array (no fragments)
  const selectItems: React.ReactNode[] = [];

  // Causali catalog group (only when the tenant has it enabled and seeded)
  if (catalogActive && absenceTypes.length > 0) {
    selectItems.push(
      <ListSubheader key="hdr-absence" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
        {t('absence.causali')}
      </ListSubheader>
    );
    selectItems.push(
      ...absenceTypes.map((a) => (
        <MenuItem
          key={`absence:${a.id}`}
          value={`absence:${a.id}`}
          onMouseDown={(e) => {
            e.preventDefault();
            handleTypeChangeUi({ target: { value: `absence:${a.id}` } });
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box
              sx={{
                width: 16, height: 16, borderRadius: '50%',
                backgroundColor: a.colour || getEventTypeColor(a.legacyType as CalendarEntryEntityTypeEnum),
                border: '1px solid #ddd'
              }}
            />
            {absenceLabel(a)}
          </Box>
        </MenuItem>
      ))
    );
  }

  selectItems.push(
    <ListSubheader key="hdr-default" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
      {t('employees.calendar.types.defaultEvents')}
    </ListSubheader>,
    ...defaultEventTypes.map((workingType) => (
      <MenuItem key={`builtin:${workingType}`} value={`builtin:${workingType}`}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box
            sx={{
              width: 16, height: 16, borderRadius: '50%',
              backgroundColor: getEventTypeColor(workingType),
              border: '1px solid #ddd'
            }}
          />
          {t(`employees.calendar.types.${workingType.toLowerCase()}`)}
        </Box>
      </MenuItem>
    )),
  );

  if (customTypes.length > 0) {
    selectItems.push(
      <ListSubheader key="hdr-custom" sx={{ fontWeight: 'bold', color: 'primary.main', mt: 1 }}>
        {t('employees.calendar.types.customEvents')}
      </ListSubheader>
    );
    selectItems.push(
      ...customTypes.map((ct) => (
        <MenuItem
          key={`custom:${ct.id}`}
          value={`custom:${ct.id}`}
          onMouseDown={(e) => {
            e.preventDefault();
            handleTypeChangeUi({ target: { value: `custom:${ct.id}` } });
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box
              sx={{
                width: 16, height: 16, borderRadius: '50%',
                backgroundColor: ct.colour || '#FF0000',
                border: '1px solid #ddd'
              }}
            />
            {ct.name}
          </Box>
        </MenuItem>
      ))
    );
  }

  console.log(selectValue);
  return (
    <StyledAppModal open={open} handleClose={onClose} style={{ maxHeight: '80vh', overflowY: 'auto' }}>
      <H2 mb={2}> {t('employees.calendar.entry.title')}</H2>
      <form onSubmit={handleSubmit}>
        <Grid container spacing={3}>
          <Grid item sm={6} xs={12}>
            <CalendarInput
              format='dd.MM.yyyy'
              onChange={(date: Date | null) => {
                formik.setFieldValue('startedOn', date);
              }}
              value={values.startedOn}
              label={t('employees.calendar.entry.startedOn')}
              slotProps={{
                textField: {
                  error: !!errors.startedOn,
                  helperText: errors.startedOn ? String(errors.startedOn) : undefined
                }
              }}
            />
          </Grid>
          <Grid item sm={6} xs={12}>
            <CalendarInput
              format='dd.MM.yyyy'
              onChange={(date: Date | null) => {
                formik.setFieldValue('endedOn', date);
              }}
              value={values.endedOn}
              label={t('employees.calendar.entry.endedOn')}
              slotProps={{
                textField: {
                  error: !!errors.endedOn,
                  helperText: errors.endedOn ? String(errors.endedOn) : undefined
                }
              }}
            />
          </Grid>

          <Grid item xs={12}>
            <FormControl fullWidth error={Boolean(touched.type && errors.type)}>
              <InputLabel>{t('employees.calendar.entry.type.label')}</InputLabel>
              <Select
                name="type"
                key={`opts-${optionsKey}`}     // e.g. "1,2"
                value={uiSelectValue}          // "builtin:ILL" or "custom:2"
                onChange={handleTypeChangeUi}
                onBlur={handleBlur}
                label={t('employees.calendar.entry.type.label')}
                IconComponent={KeyboardArrowDown}
                displayEmpty
                inputProps={{ 'aria-label': 'Event type' }}
                MenuProps={{ PaperProps: { style: { maxHeight: 300 } } }}
              >
                {selectItems}
              </Select>

              {touched.type && errors.type && (
                <Box sx={{ color: 'error.main', fontSize: '0.75rem', mt: 0.5 }}>
                  {String(errors.type)}
                </Box>
              )}
            </FormControl>
          </Grid>

          {/* Medical certificate / protocol — only for causali that require it */}
          {selectedAbsenceType?.requiresCertificate && (
            <>
              <Grid item xs={12}>
                <H4>{t('absence.certificate.section')}</H4>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>{t('absence.certificate.status')}</InputLabel>
                  <Select
                    name='certificateStatus'
                    value={values.certificateStatus ?? 'PENDING'}
                    label={t('absence.certificate.status')}
                    onChange={handleChange}
                    IconComponent={KeyboardArrowDown}
                  >
                    <MenuItem value='PENDING'>{t('absence.certificate.statusPending')}</MenuItem>
                    <MenuItem value='RECEIVED'>{t('absence.certificate.statusReceived')}</MenuItem>
                    <MenuItem value='NA'>{t('absence.certificate.statusNa')}</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  name='protocolNumber'
                  label={selectedAbsenceType?.protocolType === 'INAIL'
                    ? t('absence.certificate.protocolInail')
                    : t('absence.certificate.protocolInps')}
                  value={values.protocolNumber ?? ''}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <CalendarInput
                  format='dd.MM.yyyy'
                  value={values.certificateStartedOn ?? null}
                  onChange={(date: Date | null) => { void formik.setFieldValue('certificateStartedOn', date); }}
                  label={t('absence.certificate.startedOn')}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <CalendarInput
                  format='dd.MM.yyyy'
                  value={values.certificateEndedOn ?? null}
                  onChange={(date: Date | null) => { void formik.setFieldValue('certificateEndedOn', date); }}
                  label={t('absence.certificate.endedOn')}
                />
              </Grid>
            </>
          )}

          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Switch
                  checked={values.allDay}
                  color='primary'
                  name='allDay'
                  onChange={handleChange}
                />
              }
              label={t('employees.calendar.entry.allDay')}
            />
          </Grid>

          {!values.allDay && (
            <>
              <Grid item xs={12} sm={6}>
                <TimePicker
                  label={t('shift.startTime')}
                  value={values.startTime ? new Date(`2000-01-01T${values.startTime}`) : null}
                  onChange={(time) => {
                    if (time) {
                      const timeString = format(time, 'HH:mm');
                      setFieldValue('startTime', timeString);
                    }
                  }}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      error: Boolean(touched.startTime && errors.startTime),
                      helperText: touched.startTime && errors.startTime,
                    }
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TimePicker
                  label={t('shift.endTime')}
                  value={values.endTime ? new Date(`2000-01-01T${values.endTime}`) : null}
                  onChange={(time) => {
                    if (time) {
                      const timeString = format(time, 'HH:mm');
                      setFieldValue('endTime', timeString);
                    }
                  }}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      error: Boolean(touched.endTime && errors.endTime),
                      helperText: touched.endTime && errors.endTime,
                    }
                  }}
                />
              </Grid>
            </>
          )}

          <Grid item xs={12}>
            <TextField
              fullWidth
              multiline
              rows={3}
              name="publicNotes"
              label={t('employees.calendar.entry.publicNotes')}
              value={values.publicNotes}
              onChange={handleChange}
              onBlur={handleBlur}
              error={Boolean(touched.publicNotes && errors.publicNotes)}
              helperText={touched.publicNotes && errors.publicNotes}
              placeholder={t('employees.calendar.notesPlaceholder')}
            />
          </Grid>

          {canViewManagerNotes && (
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                name="managerNotes"
                label={t('employees.calendar.entry.managerNotes')}
                value={values.managerNotes}
                onChange={handleChange}
                onBlur={handleBlur}
                error={Boolean(touched.managerNotes && errors.managerNotes)}
                helperText={touched.managerNotes && errors.managerNotes}
                placeholder={t('employees.calendar.notesPlaceholder')}
              />
            </Grid>
          )}
        </Grid>
        {employees?.length && (
          <FlexBox flexDirection={'column'} marginTop={'2rem'} gap={2}>
            <H4>{t('employees.title')}</H4>
            {employees.map((employee: any) => {
              return (
                <FlexBox key={employee.original.id} justifyContent={'space-between'} alignItems={'center'}>
                  <span>• {employee.original.firstName} {employee.original.lastName}</span>
                </FlexBox>
              );
            })}
          </FlexBox>
        )
        }

        <FlexBox justifyContent='flex-end' gap={2} marginTop={4}>
          <Button fullWidth size='small' variant='outlined' onClick={onClose}>
            {t('common.forms.button.cancel')}
          </Button>
          <Button fullWidth size='small' type='submit' variant='contained'>
            {t('common.forms.button.save')}
          </Button>
        </FlexBox>
      </form>
    </StyledAppModal>
  );
};

// Helper function to get colors for default event types
function getEventTypeColor(type: CalendarEntryEntityTypeEnum): string {
  switch (type) {
    case CalendarEntryEntityTypeEnum.HalfDay:
      return '#00eaff';
    case CalendarEntryEntityTypeEnum.DayOff:
      return '#ff9800';
    case CalendarEntryEntityTypeEnum.Vacation:
      return '#4caf50';
    case CalendarEntryEntityTypeEnum.Ill:
      return '#f44336';
    case CalendarEntryEntityTypeEnum.UnpaidLeave:
      return '#6c25be';
    default:
      return '#6c757d';
  }
}

export default AddEventModal;
